import { Button } from "@/components/ui/button"
import { usePlatform } from "@/platform/context"
import type { ManagedPlugin } from "@/platform/types"
import { useAuth } from "@/providers/auth"
import { Home, LogOut, SlidersHorizontal, X } from "lucide-react"
import {
	useCallback,
	useEffect,
	useMemo,
	useState,
	type ReactNode,
} from "react"
import { useLocation, useNavigate } from "react-router"

type ProjectTab = {
	id: string
	title: string
}

const projectIDFromPath = (pathname: string) => {
	const match = /^\/projects\/([^/]+)/.exec(pathname)
	return match?.[1] ? decodeURIComponent(match[1]) : null
}

const projectTitleFromLocation = (
	locationState: unknown,
	projectID: string,
) => {
	if (
		locationState &&
		typeof locationState === "object" &&
		"projectTitle" in locationState &&
		typeof locationState.projectTitle === "string" &&
		locationState.projectTitle.trim().length > 0
	) {
		return locationState.projectTitle.trim()
	}

	return projectID
}

const healthForPlugins = (plugins: ManagedPlugin[]) => {
	if (plugins.length === 0) return "hidden" as const
	if (plugins.some((plugin) => plugin.statusTone === "danger")) {
		return "error" as const
	}
	if (
		plugins.some(
			(plugin) =>
				plugin.statusTone === "warning" ||
				plugin.hasUpdateAvailable ||
				plugin.installState !== "installed",
		)
	) {
		return "attention" as const
	}
	return "healthy" as const
}

export const OriginShell = ({ children }: { children: ReactNode }) => {
	const { initialised, logout, status, user } = useAuth()
	const platform = usePlatform()
	const location = useLocation()
	const navigate = useNavigate()
	const [projectTabs, setProjectTabs] = useState<ProjectTab[]>([])
	const [pluginHealth, setPluginHealth] = useState<
		"hidden" | "healthy" | "attention" | "error"
	>("hidden")

	const refreshPluginHealth = useCallback(async () => {
		const plugins = (await platform.getManagedPlugins?.()) ?? []
		setPluginHealth(healthForPlugins(plugins))
	}, [platform])

	const activeProjectID = useMemo(
		() => projectIDFromPath(location.pathname),
		[location.pathname],
	)
	const isInitialRoute = location.pathname === "/"
	const activeSurface = activeProjectID
		? `project:${activeProjectID}`
		: location.pathname === "/origin/manage" ||
				location.pathname === "/origin/runtime"
			? "manage"
			: "home"
	const surfaceClassName = activeProjectID
		? "origin-shell-surface origin-shell-surface-project"
		: activeSurface === "manage"
			? "origin-shell-surface origin-shell-surface-manage"
			: "origin-shell-surface origin-shell-surface-home"

	useEffect(() => {
		if (!initialised) return
		if (location.pathname === "/") {
			navigate(status === "authenticated" ? "/dashboard" : "/login", {
				replace: true,
			})
			return
		}
		if (status === "unauthenticated" && location.pathname !== "/login") {
			navigate("/login", { replace: true })
		}
	}, [initialised, location.pathname, navigate, status])

	useEffect(() => {
		if (status === "authenticated") void refreshPluginHealth()
		else setPluginHealth("hidden")
	}, [refreshPluginHealth, status])

	useEffect(() => {
		const cleanups = [
			platform.onDeepLink?.((path) => navigate(path)),
			platform.onMenuNavigate?.((path) => navigate(path)),
			platform.onPluginInstallProgress?.((progress) => {
				if (progress.step === "Failed") setPluginHealth("error")
				else if (progress.step === "Installed") void refreshPluginHealth()
				else setPluginHealth("attention")
			}),
		]
		return () => {
			for (const cleanup of cleanups) cleanup?.()
		}
	}, [navigate, platform, refreshPluginHealth])

	useEffect(() => {
		if (!activeProjectID) {
			return
		}

		const title = projectTitleFromLocation(location.state, activeProjectID)
		setProjectTabs((current) => {
			if (current.some((tab) => tab.id === activeProjectID)) {
				return current.map((tab) =>
					tab.id === activeProjectID && tab.title === tab.id
						? { ...tab, title }
						: tab,
				)
			}
			return [...current, { id: activeProjectID, title }]
		})
	}, [activeProjectID, location.state])

	useEffect(() => {
		const updateProjectTitle = (event: Event) => {
			const detail = (event as CustomEvent<unknown>).detail
			if (
				!detail ||
				typeof detail !== "object" ||
				!("projectID" in detail) ||
				!("title" in detail) ||
				typeof detail.projectID !== "string" ||
				typeof detail.title !== "string"
			) {
				return
			}

			const title = detail.title.trim()
			if (!title) {
				return
			}

			setProjectTabs((current) =>
				current.map((tab) =>
					tab.id === detail.projectID ? { ...tab, title } : tab,
				),
			)
		}

		window.addEventListener("origin:project-title", updateProjectTitle)
		return () => {
			window.removeEventListener("origin:project-title", updateProjectTitle)
		}
	}, [])

	const closeProjectTab = useCallback(
		(projectID: string) => {
			setProjectTabs((current) => current.filter((tab) => tab.id !== projectID))
			if (activeProjectID === projectID) {
				navigate("/dashboard")
			}
		},
		[activeProjectID, navigate],
	)

	const openHome = () => {
		navigate(status === "authenticated" ? "/dashboard" : "/login")
	}

	const signOut = () => {
		void logout().finally(() => navigate("/login", { replace: true }))
	}

	return (
		<div className="origin-shell-root">
			<header className="origin-shell-topbar">
				<div className="origin-traffic-spacer" />

				<nav className="origin-tab-strip" aria-label="Workspace tabs">
					<Button
						type="button"
						variant="ghost"
						className={`origin-tab origin-tab-icon ${activeSurface === "home" ? "is-active" : ""}`}
						onClick={openHome}
						title="Home"
					>
						<Home className="h-3.5 w-3.5" />
					</Button>

					{projectTabs.map((tab) => (
						<div className="origin-project-tab" key={tab.id}>
							<Button
								type="button"
								variant="ghost"
								className={`origin-tab origin-tab-project ${activeSurface === `project:${tab.id}` ? "is-active" : ""}`}
								onClick={() => navigate(`/projects/${tab.id}`)}
							>
								<span>{tab.title}</span>
							</Button>
							<Button
								type="button"
								variant="ghost"
								className="origin-tab-close"
								onClick={() => closeProjectTab(tab.id)}
								title="Close project"
							>
								<X className="h-3 w-3" />
							</Button>
						</div>
					))}
				</nav>

				<div className="origin-shell-actions">
					<Button
						type="button"
						variant="ghost"
						className={`origin-tab origin-tab-action ${activeSurface === "manage" ? "is-active" : ""}`}
						onClick={() => navigate("/origin/manage")}
						title="Manage"
					>
						<SlidersHorizontal className="h-3.5 w-3.5" />
						<span>Manage</span>
						{pluginHealth !== "hidden" ? (
							<span
								className={`h-2 w-2 rounded-full ${
									pluginHealth === "healthy"
										? "bg-emerald-500"
										: pluginHealth === "error"
											? "bg-destructive"
											: "bg-amber-500"
								}`}
								title={`Plugin health: ${pluginHealth}`}
							/>
						) : null}
					</Button>
					{user ? (
						<>
							<span className="origin-account-label">{user.email}</span>
							<Button
								type="button"
								variant="ghost"
								className="origin-tab origin-tab-icon"
								onClick={signOut}
								title="Sign out"
							>
								<LogOut className="h-3.5 w-3.5" />
							</Button>
						</>
					) : null}
				</div>
			</header>

			<div className={surfaceClassName}>
				{isInitialRoute ? (
					<div className="flex h-full items-center justify-center text-sm text-muted-foreground">
						Loading…
					</div>
				) : (
					children
				)}
			</div>
		</div>
	)
}
